import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, vShow as _vShow, withCtx as _withCtx, createVNode as _createVNode, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2d708e2c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "y-units" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "axis-line" }, null, -1))
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "y-axis" }, null, -1))
const _hoisted_4 = { class: "water-block-main" }
const _hoisted_5 = { class: "alert-lines" }
const _hoisted_6 = { class: "alert-title" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "alert-lines-bg" }, null, -1))
const _hoisted_8 = { class: "water-block-blue-wrapper" }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("br", null, null, -1))
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "water-overlay-bottom" }, null, -1))

import { computed, ref, onMounted, onUnmounted } from 'vue'
import { times } from 'lodash'
import moment from 'moment'
import Tooltip from '@/components/common/Tooltip.vue'
import { Threshold } from '@/models/InitData'

type WaterDiagramProps = {
  value?: number // 現在水位
  // 閾値
  thresholds: {
    [key: string]: Threshold
  }
  scale: {
    min: number // 断面図底端
    max: number // 断面図天端
    span: number // Y軸単位の分割数
  }
  type?: 'water-detail' | 'data-summary' // 表示場所
  ap?: number // 水位基準
  showTooltip?: boolean
  time?: string // 取得時間
}

type ThresholdsKey = keyof WaterDiagramProps['thresholds']


export default /*#__PURE__*/_defineComponent({
  props: {
    value: null,
    thresholds: null,
    scale: null,
    type: null,
    ap: null,
    showTooltip: { type: Boolean },
    time: null
  },
  setup(__props: any) {

const props = __props as {
  value?: number // 現在水位
  // 閾値
  thresholds: {
    [key: string]: Threshold
  }
  scale: {
    min: number // 断面図底端
    max: number // 断面図天端
    span: number // Y軸単位の分割数
  }
  type?: 'water-detail' | 'data-summary' // 表示場所
  ap?: number // 水位基準
  showTooltip?: boolean
  time?: string // 取得時間
}



const OVERLAY_HEIGHT = 288
const OVERLAY_TOP = 246
const OVERLAY_OFFSET = 11
const ALERT_KEYS = ['none', 'water_warn1', 'water_warn2', 'water_warn3', 'water_warn4']
const MOUSEOVER_MARGIN = 5
const ALERT_TYPES = {
  none: '',
  water_warn1: 'notice',
  water_warn2: 'warn',
  water_warn3: 'danger',
  water_warn4: 'special',
}
const ALERT_TYPES2 = {
  none: 'notice',
  water_warn1: 'warn',
  water_warn2: 'special',
  water_warn3: 'danger',
  water_warn4: '',
}

const showTooltip = ref<boolean>(false)
const tooltipBottom = ref<number>(0)
const tooltipLeft = ref<number>(0)
const windowWidth = ref(window.innerWidth)

const tooltip = {
  title: '',
  value: 0,
}

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  handleResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const alertType = (key: string) => {
  const alertTypes = props.ap === undefined ? ALERT_TYPES : ALERT_TYPES2

  return alertTypes[key as keyof typeof ALERT_TYPES]
}

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

const alertLines = computed(() => {
  return Object.keys(props.thresholds).map(k => {
    const t = props.thresholds[k]
    const className = `alert-line-${alertType(k)}`

    return {
      style: lineStyle(k),
      threshold: calcThFromAP(t.comparison),
      key: k,
      className,
    }
  })
})

const typeIsWaterDetail = computed(() => props.type === 'water-detail')

const isBigSize = computed(() => {
  return windowWidth.value >= 1920
})

const maxHeight = computed(() => {
  return isBigSize.value ? 240 : typeIsWaterDetail.value ? 243 : 141
})

const waterHeight = computed(() => {
  if (props.value === undefined) {
    return 0
  }

  const diff = props.scale.max - props.scale.min

  return (maxHeight.value / diff) * (props.value - props.scale.min)
})

const linePositions = computed(() => {
  const thresholds = props.thresholds

  return ALERT_KEYS.map(key => {
    const target = thresholds[key as ThresholdsKey]

    if (target === undefined) {
      return
    }
    const bottom = calcHeight(target.comparison)

    if (!bottom) {
      return
    }

    return { bottom, key }
  }).filter(p => p)
})

const yUnits = computed(() => {
  const { scale } = props
  const diff = scale.max - scale.min
  const span = diff / scale.span
  const units = [] as number[]
  const ap = props.ap || 0

  times(scale.span + 1, index => {
    units.unshift(scale.min + index * span + ap)
  })

  return units
})

const yUnitStyle = computed(() => {
  const { scale } = props
  const height = maxHeight.value / scale.span

  return {
    height: `${height}px`,
  }
})

const calcHeight = (value: number) => {
  if (value === undefined) {
    return undefined
  }

  const diff = props.scale.max - props.scale.min

  return (maxHeight.value / diff) * (value - props.scale.min)
}

const calcThFromAP = (th: number) => {
  if (props.ap === undefined) {
    return
  }

  return (th + props.ap).toFixed(2)
}

const lineStyle = (key: string) => {
  const value = props.thresholds[key]?.comparison
  const bottom = calcHeight(value)

  if (bottom === undefined) {
    return { display: 'none' }
  }

  return {
    bottom: `${bottom}px`,
  }
}

const heightStyle = (height: number) => {
  return {
    height: `${height}px`,
  }
}

const onMousemove = (e: MouseEvent) => {
  if (!props.showTooltip) {
    return
  }

  const y = OVERLAY_HEIGHT + OVERLAY_TOP - e.pageY - 15 - OVERLAY_OFFSET
  const ap = props.ap || 0

  showTooltip.value = false

  if (props.value === undefined) {
    return
  }

  if (y >= 0 && y <= waterHeight.value) {
    tooltipBottom.value = y
    tooltipLeft.value = 100
    showTooltip.value = true
    tooltip.title = moment(props.time).format('HH時mm分')
    tooltip.value = (props.value || 0) + ap
  }

  linePositions.value.forEach(position => {
    if (position === undefined) {
      return
    }

    if (y > position.bottom - MOUSEOVER_MARGIN && y < position.bottom + MOUSEOVER_MARGIN) {
      const target = props.thresholds[position.key as ThresholdsKey]

      if (!target) {
        return
      }

      tooltipBottom.value = y
      tooltipLeft.value = 100
      showTooltip.value = true

      tooltip.title = target?.title
      tooltip.value = (target?.comparison || 0) + ap
    }
  })
}

const onMouseleave = () => {
  showTooltip.value = false
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([`${__props.type} ${__props.ap ? 'ap' : ''}`, "water-block"])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(yUnits), (unit) => {
        return (_openBlock(), _createElementBlock("div", {
          style: _normalizeStyle(_unref(yUnitStyle)),
          key: unit,
          class: "y-unit"
        }, [
          _createTextVNode(_toDisplayString(unit.toFixed(1)) + "m", 1),
          _hoisted_2
        ], 4))
      }), 128))
    ]),
    _hoisted_3,
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(alertLines), (line) => {
          return (_openBlock(), _createElementBlock("div", {
            key: line.key,
            style: _normalizeStyle(line.style),
            class: _normalizeClass([line.className, "alert-line"])
          }, [
            _createElementVNode("div", _hoisted_6, "A.P. +" + _toDisplayString(line.threshold) + "m", 1)
          ], 6))
        }), 128))
      ]),
      _hoisted_7,
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", {
          class: "water-block-blue",
          style: _normalizeStyle(heightStyle(_unref(waterHeight)))
        }, null, 4)
      ]),
      _createElementVNode("div", {
        onMousemove: onMousemove,
        onMouseleave: onMouseleave,
        class: "water-overlay"
      }, [
        _withDirectives(_createVNode(Tooltip, {
          bottom: tooltipBottom.value,
          left: tooltipLeft.value,
          class: "hide-sp"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(tooltip?.title), 1),
            _hoisted_9,
            _createTextVNode(" " + _toDisplayString(tooltip?.value.toFixed(2)) + "m ", 1)
          ]),
          _: 1
        }, 8, ["bottom", "left"]), [
          [_vShow, showTooltip.value]
        ]),
        _hoisted_10
      ], 32)
    ])
  ], 2))
}
}

})